
import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading, refreshSession } = useAuth();
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [timeElapsed, setTimeElapsed] = useState(0);
  
  // Ajouter un timer pour suivre le temps écoulé
  useEffect(() => {
    if (loading) {
      const startTime = Date.now();
      const interval = setInterval(() => {
        setTimeElapsed(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    } else {
      setTimeElapsed(0);
    }
  }, [loading]);

  // Effet pour gérer les erreurs d'authentification persistantes
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    if (loading && !user) {
      // Vérifier l'état après 3 secondes (réduit de 5 à 3 pour plus de réactivité)
      timeoutId = setTimeout(async () => {
        try {
          console.log("Vérification de session après 3 secondes de chargement");
          // Vérifier directement la session auprès de Supabase
          const { data } = await supabase.auth.getSession();
          if (!data.session) {
            console.log("Pas de session détectée après 3 secondes");
            setError("Session expirée ou invalide");
          } else if (!user) {
            // La session existe mais pas l'utilisateur, essayer de rafraîchir
            console.log("Session trouvée mais utilisateur non défini, rafraîchissement");
            await refreshSession();
          }
        } catch (err) {
          console.error("Erreur lors de la vérification de session:", err);
          setError("Erreur de connexion au serveur d'authentification");
        }
      }, 3000);
    }
    
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [loading, user, refreshSession, retryCount]);

  // Gérer une tentative de reconnexion
  const handleRetry = async () => {
    setError(null);
    setRetryCount(prev => prev + 1);
    
    try {
      // Informer l'utilisateur
      toast.info("Tentative de reconnexion en cours...");
      
      // Forcer le rafraîchissement de la session
      await refreshSession();
    } catch (err) {
      console.error("Erreur lors de la tentative de reconnexion:", err);
      setError("Échec de la reconnexion");
      
      toast.error("Échec de la reconnexion", {
        description: "Impossible de rétablir votre session. Veuillez essayer de vous reconnecter."
      });
    }
  };

  // État de chargement
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <div className="w-full max-w-md p-6 bg-white rounded-lg shadow-sm">
          <h2 className="text-xl font-semibold text-center mb-6">Vérification de votre session</h2>
          
          <div className="flex flex-col items-center space-y-4">
            <Loader2 className="h-12 w-12 animate-spin text-primary" />
            
            <p className="text-muted-foreground text-center">
              Vérification de l'authentification...
              {timeElapsed > 2 && ` (${timeElapsed}s)`}
            </p>
            
            {/* Afficher un message si le chargement prend du temps */}
            {timeElapsed > 5 && !error && (
              <p className="text-amber-600 text-sm text-center mt-2">
                La vérification prend plus de temps que prévu. Veuillez patienter...
              </p>
            )}
            
            {/* Afficher un bouton de réessai après 7 secondes ou si erreur */}
            {(error || timeElapsed > 7) && (
              <div className="mt-6 w-full">
                {error && (
                  <div className="flex items-center text-red-500 mb-3 justify-center">
                    <AlertCircle className="h-5 w-5 mr-2" />
                    <p>{error}</p>
                  </div>
                )}
                
                <div className="flex gap-3 justify-center mt-4">
                  <Button 
                    onClick={handleRetry} 
                    variant="outline"
                  >
                    Réessayer
                  </Button>
                  
                  <Button
                    variant="default"
                    asChild
                  >
                    <a href="/login">Se connecter</a>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Si utilisateur non authentifié, rediriger vers login avec le chemin de retour
  if (!user) {
    console.log("Redirection vers login depuis ProtectedRoute:", location.pathname);
    return <Navigate to="/login" state={{ returnTo: location.pathname }} replace />;
  }

  // Utilisateur authentifié - afficher le contenu protégé
  return <>{children}</>;
};

export default ProtectedRoute;
